import { Dispatch, SetStateAction } from 'react';
import { Tooltip } from 'react-bootstrap';
import cn from 'classnames';
import { Brand100 } from '@src/utils/colors';
import { Placement, Size, SizeType } from '../designConstants';
import { Dropdown } from '../Dropdown';
import { DropdownToggleType } from '../Dropdown/dropdown-constants';
import './DimensionHeaderLabel.scss';

interface DimensionHeaderLabelProps {
  dimensionTypeName: string;
  label: string;
  headerHeight: number;
  textAlign: 'left' | 'center' | 'right';
  hasThumbnail: boolean;
  setSelectedThumbnailSize: Dispatch<SetStateAction<SizeType>>;
}

// Row-density icons for the thumbnail-size dropdown — small/medium/large differ
// only in the number of bars and their `d=` path data.
const SMALL_DENSITY_PATHS = [
  'M19.5 2C19.7761 2 20 2.22386 20 2.5V4.5C20 4.77614 19.7761 5 19.5 5H2.5C2.22386 5 2 4.77614 2 4.5V2.5C2 2.22386 2.22386 2 2.5 2H19.5Z',
  'M19.5 7C19.7761 7 20 7.22386 20 7.5V9.5C20 9.77614 19.7761 10 19.5 10H2.5C2.22386 10 2 9.77614 2 9.5V7.5C2 7.22386 2.22386 7 2.5 7H19.5Z',
  'M19.5 12C19.7761 12 20 12.2239 20 12.5V14.5C20 14.7761 19.7761 15 19.5 15H2.5C2.22386 15 2 14.7761 2 14.5V12.5C2 12.2239 2.22386 12 2.5 12H19.5Z',
  'M19.5 17C19.7761 17 20 17.2239 20 17.5V19.5C20 19.7761 19.7761 20 19.5 20H2.5C2.22386 20 2 19.7761 2 19.5V17.5C2 17.2239 2.22386 17 2.5 17H19.5Z'
];

const MEDIUM_DENSITY_PATHS = [
  'M19.5 2C19.7761 2 20 2.22386 20 2.5V6.16667C20 6.44281 19.7761 6.66667 19.5 6.66667H2.5C2.22386 6.66667 2 6.44281 2 6.16667V2.5C2 2.22386 2.22386 2 2.5 2H19.5Z',
  'M19.5 8.66699C19.7761 8.66699 20 8.89085 20 9.16699V12.8337C20 13.1098 19.7761 13.3337 19.5 13.3337H2.5C2.22386 13.3337 2 13.1098 2 12.8337V9.16699C2 8.89085 2.22386 8.66699 2.5 8.66699H19.5Z',
  'M19.5 15.333C19.7761 15.333 20 15.5569 20 15.833V19.4997C20 19.7758 19.7761 19.9997 19.5 19.9997H2.5C2.22386 19.9997 2 19.7758 2 19.4997V15.833C2 15.5569 2.22386 15.333 2.5 15.333H19.5Z'
];

const LARGE_DENSITY_PATHS = [
  'M19.5 2C19.7761 2 20 2.22386 20 2.5V9.5C20 9.77614 19.7761 10 19.5 10H2.5C2.22386 10 2 9.77614 2 9.5V2.5C2 2.22386 2.22386 2 2.5 2H19.5Z',
  'M19.5 12C19.7761 12 20 12.2239 20 12.5V19.5C20 19.7761 19.7761 20 19.5 20H2.5C2.22386 20 2 19.7761 2 19.5V12.5C2 12.2239 2.22386 12 2.5 12H19.5Z'
];

const RowDensityIcon = ({ paths }: { paths: string[] }) => (
  <svg width='22' height='22' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg'>
    {paths.map(d => (
      <path key={d} d={d} fill='black' />
    ))}
  </svg>
);

const ThumbnailToggleIcon = () => (
  <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M15.0534 2.35742C15.3788 2.35742 15.6426 2.62125 15.6426 2.94671V3.32171C15.6426 3.64716 15.3788 3.91099 15.0534 3.91099H2.94622C2.62077 3.91099 2.35693 3.64716 2.35693 3.32171V2.94671C2.35693 2.62125 2.62077 2.35742 2.94622 2.35742H15.0534Z'
      fill={Brand100}
    />
    <path
      d='M15.0534 6.26758C15.3788 6.26758 15.6426 6.53141 15.6426 6.85686V7.23186C15.6426 7.55732 15.3788 7.82115 15.0534 7.82115H2.94622C2.62077 7.82115 2.35693 7.55732 2.35693 7.23186V6.85686C2.35693 6.53141 2.62077 6.26758 2.94622 6.26758H15.0534Z'
      fill={Brand100}
    />
    <path
      d='M15.0534 10.1787C15.3788 10.1787 15.6426 10.4425 15.6426 10.768V11.143C15.6426 11.4684 15.3788 11.7323 15.0534 11.7323H2.94622C2.62077 11.7323 2.35693 11.4685 2.35693 11.143V10.768C2.35693 10.4425 2.62077 10.1787 2.94622 10.1787H15.0534Z'
      fill={Brand100}
    />
    <path
      d='M15.0534 14.0889C15.3788 14.0889 15.6426 14.3527 15.6426 14.6782V15.0532C15.6426 15.3786 15.3788 15.6424 15.0534 15.6424H2.94622C2.62077 15.6424 2.35693 15.3786 2.35693 15.0532V14.6782C2.35693 14.3527 2.62077 14.0889 2.94622 14.0889H15.0534Z'
      fill={Brand100}
    />
  </svg>
);

const THUMBNAIL_SIZE_OPTIONS = [
  {
    label: 'Small',
    value: Size.SM,
    leadingContent: (
      <div className='density-option'>
        <RowDensityIcon paths={SMALL_DENSITY_PATHS} />
      </div>
    )
  },
  {
    label: 'Medium',
    value: Size.MD,
    leadingContent: (
      <div className='density-option'>
        <RowDensityIcon paths={MEDIUM_DENSITY_PATHS} />
      </div>
    )
  },
  {
    label: 'Large',
    value: Size.LG,
    leadingContent: (
      <div className='density-option'>
        <RowDensityIcon paths={LARGE_DENSITY_PATHS} />
      </div>
    )
  }
] as const;

export const DimensionHeaderLabel = ({
  dimensionTypeName,
  label,
  headerHeight,
  textAlign,
  hasThumbnail,
  setSelectedThumbnailSize
}: DimensionHeaderLabelProps) => {
  if (dimensionTypeName === 'Ad Name' && hasThumbnail) {
    return (
      <div
        className={cn('table-header-label', 'with-density-toggle')}
        style={{ height: headerHeight, textAlign }}
      >
        <Dropdown
          type={DropdownToggleType.ICON_ONLY}
          overlayPlacement={Placement.RIGHT}
          options={THUMBNAIL_SIZE_OPTIONS}
          onChange={setSelectedThumbnailSize}
          leadingIcon={<ThumbnailToggleIcon />}
          size={Size.SM}
          overlay={<Tooltip id='iconSize'>Icon Size</Tooltip>}
          className='ad-name-header-dropdown'
        />
        <span>{label}</span>
      </div>
    );
  }

  return (
    <div className={cn('table-header-label', 'padded')} style={{ height: headerHeight, textAlign }}>
      {label}
    </div>
  );
};
